import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Home, UtensilsCrossed, BookUser, ShoppingBag, HandPlatter, X, Volume2, VolumeX, Loader2, Store, ExternalLink, Star, Gamepad2 } from "lucide-react";
import { api, USER_ID, track, orderHandoff } from "./api";
import { getPicks, setPickQty, clearPicks, picksCount, picksPending, resyncPicks } from "./picks";
import { FlagsProvider, useFlags, flagOn } from "./flags";
import { SoundProvider, useSound } from "./sound";
import { DineInPanel } from "./DineIn";
import AttractScreen from "./AttractScreen";
import ErrorBoundary from "./ErrorBoundary";
import Mascot from "./Mascot";
import { LangProvider, useLang, useT } from "./i18n";
import { ReviewThanks } from "./ReviewThanks";
import { ContentProvider, useContent, useContentValue, Editable } from "./content";
import { MapPin, Clock, Phone, FileText } from "lucide-react";
import "./theme.css";
import "./theme-light.css";
import { ThemeToggle, useThemePref } from "./ThemeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IDLE_TIMEOUT_MS = 3 * 60 * 1000; // kiosk: reset to start screen after 3 idle minutes

const Header = () => {
  const { toggleSound, voiceReady, rawSoundOn } = useSound();
  const { lang } = useLang();
  const [greet, setGreet] = useState("idle");
  const stopGreeting = () => {
    window.__xpGreetAudio?.pause();
    window.__xpGreetAudio = null;
    setGreet("idle");
  };
  const playGreeting = async () => {
    setGreet("loading");
    try {
      track("greeting_tapped", { language: lang });
      const r = await api.post("/greeting/speak", { user_id: USER_ID, language: lang });
      if (!r.data.audio_url) return setGreet("idle");
      // one reusable element — iPad Safari blocks a second Audio() started outside the tap gesture
      const a = new Audio();
      window.__xpGreetAudio = a;
      const mainUrl = `${BACKEND_URL}${r.data.audio_url}`;
      const playMain = () => {
        a.onended = () => setGreet("idle");
        a.src = mainUrl;
        a.play().catch(() => setGreet("idle"));
      };
      if (r.data.prelude_url) {
        a.onended = playMain;
        a.src = `${BACKEND_URL}${r.data.prelude_url}`;
      } else {
        a.onended = () => setGreet("idle");
        a.src = mainUrl;
      }
      await a.play();
      setGreet("playing");
    } catch {
      setGreet("idle");
    }
  };
  return (
    <header data-testid="xp-header" className="xp-header sticky top-0 z-50 flex items-center justify-between px-4 h-16">
      <NavLink to="/experience" data-testid="header-logo" className="flex items-center gap-2.5 font-display text-base font-bold tracking-wide">
        <img src="/urban-emblem.png" alt="Urban by VeerGi emblem" className="h-9 w-auto" />
        <span className="whitespace-nowrap text-[15px]">URBAN <span className="text-[#D4AF37]">BY VEERGI</span></span>
      </NavLink>
      <div className="flex items-center gap-2">
        <TopNavPages />
        {!window.location.pathname.startsWith("/experience/admin") && <ThemeToggle />}
        {voiceReady && (
          <button
            data-testid="sound-toggle"
            onClick={() => {
              if (rawSoundOn) {
                stopGreeting();
                toggleSound();
              } else {
                toggleSound();
                playGreeting();
              }
            }}
            aria-label={rawSoundOn ? "Turn VeerGi's voice off" : "Turn VeerGi's voice on"}
            title={rawSoundOn ? "VeerGi voice: ON" : "VeerGi voice: OFF — tap to turn on"}
            className="xp-iconbtn"
            style={rawSoundOn ? { background: "#D4AF37", color: "#0A0A0A", borderColor: "#D4AF37" } : undefined}
          >
            {greet === "loading" ? <Loader2 size={17} className="animate-spin" /> : rawSoundOn ? <Volume2 size={17} /> : <VolumeX size={17} />}
          </button>
        )}
      </div>
    </header>
  );
};

const ORDER_STATUS_UI = {
  submitted_unpaid: { label: "WAITING FOR CONFIRMATION", sub: "The counter confirms unpaid orders before packing.", color: "#E2A33D" },
  paid: { label: "PAYMENT RECEIVED", sub: "Your order is heading straight to the kitchen.", color: "#2ECC71" },
  accepted: { label: "ORDER CONFIRMED", sub: "The kitchen has your order.", color: "#2ECC71" },
  preparing: { label: "BEING PREPARED", sub: "Fresh from our kitchen, coming right up.", color: "#2ECC71" },
  ready: { label: "READY!", sub: "", color: "#2ECC71" },
  out_for_delivery: { label: "OUT FOR DELIVERY", sub: "On its way to you.", color: "#2ECC71" },
};

const OrderSheet = ({ open, onClose }) => {
  const t = useT();
  const [picks, setPicks] = useState(getPicks);
  const [pending, setPending] = useState(picksPending);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [reviewUrl, setReviewUrl] = useState("");
  const [thanksOpen, setThanksOpen] = useState(false);
  const [placing, setPlacing] = useState("");
  const [sent, setSent] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  useEffect(() => {
    const sync = () => setPicks(getPicks());
    const onPending = () => setPending(picksPending());
    window.addEventListener("xp-picks-changed", sync);
    window.addEventListener("xp-picks-pending", onPending);
    window.addEventListener("online", onPending);
    return () => {
      window.removeEventListener("xp-picks-changed", sync);
      window.removeEventListener("xp-picks-pending", onPending);
      window.removeEventListener("online", onPending);
    };
  }, []);
  useEffect(() => {
    if (open) api.get("/settings").then((r) => { setCheckoutUrl(r.data.checkout_url || ""); setReviewUrl(r.data.review_url || ""); }).catch(() => {});
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const load = () => api.get(`/checkout/mine?user_id=${USER_ID}`).then((r) => setOrderInfo(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [open]);
  if (!open) return null;
  const orderable = picks.filter((p) => p.brand !== "grocery");
  const grocery = picks.filter((p) => p.brand === "grocery");
  const orderVia = async (fulfillment) => {
    setPlacing(fulfillment);
    try {
      const r = await api.post("/checkout/handoff", {
        user_id: USER_ID, fulfillment,
        items: orderable.map(({ slug, name, price, qty, brand }) => ({ slug, name, price, qty, brand })),
      });
      track("checkout_handoff", { fulfillment, code: r.data.code });
      setSent({ code: r.data.code, url: r.data.url, fulfillment });
      window.open(r.data.url, "_blank", "noopener");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout — please try again");
    }
    setPlacing("");
  };
  const reorderLast = async () => {
    const lp = orderInfo?.last_paid;
    if (!lp) return;
    setPlacing("reorder");
    try {
      const r = await api.post("/checkout/handoff", {
        user_id: USER_ID, fulfillment: lp.fulfillment,
        items: lp.items.map(({ slug, name, price, qty, brand }) => ({ slug, name, price, qty, brand })),
      });
      track("checkout_reorder", { code: r.data.code, fulfillment: lp.fulfillment });
      setSent({ code: r.data.code, url: r.data.url, fulfillment: lp.fulfillment });
      window.open(r.data.url, "_blank", "noopener");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reorder — please try again");
    }
    setPlacing("");
  };
  const activeUi = orderInfo?.active ? ORDER_STATUS_UI[orderInfo.active.status] : null;
  return (
    <div data-testid="order-sheet" className="fixed inset-0 z-[70] bg-black/70 flex justify-center items-end" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md md:max-w-lg lg:max-w-xl bg-[#121212] rounded-t-3xl border-t border-[#D4AF37]/40 p-6 pb-10 text-left max-h-[85vh] overflow-y-auto"
      >
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2 text-[#D4AF37]">
            <HandPlatter size={18} />
            <span className="text-[10px] tracking-[0.3em]">{t("order.kicker")}</span>
          </div>
          <button data-testid="order-sheet-close" onClick={onClose} aria-label="Close"><X size={20} className="text-white/60" /></button>
        </div>
        {activeUi && (
          <div data-testid="order-status-panel" className="mt-4 rounded-2xl border p-4"
            style={{ borderColor: `${activeUi.color}66`, background: `${activeUi.color}14` }}>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full animate-pulse shrink-0" style={{ background: activeUi.color }} />
              <p data-testid="order-status-label" className="text-xs font-black tracking-widest" style={{ color: activeUi.color }}>
                {orderInfo.active.status === "ready"
                  ? (orderInfo.active.fulfillment === "pickup" ? "READY AT THE FRONT DESK!" : "READY — HEADING TO YOUR DRIVER")
                  : activeUi.label}
              </p>
            </div>
            <p className="text-[11px] text-white/55 mt-1.5 leading-relaxed">
              {orderInfo.active.status === "ready" && orderInfo.active.fulfillment === "pickup"
                ? "Come grab it — it's hot and waiting for you."
                : activeUi.sub}
            </p>
            <p className="text-[9px] text-white/35 mt-1.5 font-bold tracking-widest uppercase">
              {orderInfo.active.fulfillment} ORDER · CODE {orderInfo.active.code} · ${orderInfo.active.total.toFixed(2)}
            </p>
          </div>
        )}
        {sent && (
          <div data-testid="picks-handoff-sent" className="mt-4 rounded-2xl border border-[#D4AF37]/50 bg-[#1C160A] p-4 text-center">
            <p className="text-xs font-black text-[#D4AF37] tracking-widest">CART SENT TO CHECKOUT · CODE {sent.code}</p>
            <p className="text-[10px] text-white/50 mt-1.5 leading-relaxed">
              Finish your prepaid {sent.fulfillment} order in the tab that just opened. Your picks stay here until you're done.
            </p>
            <button data-testid="picks-reopen-checkout" onClick={() => window.open(sent.url, "_blank", "noopener")}
              className="mt-3 w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-bold rounded-full py-3 text-xs tracking-widest">
              <ExternalLink size={13} /> RE-OPEN CHECKOUT
            </button>
            <button data-testid="picks-handoff-reset" onClick={() => setSent(null)}
              className="mt-2 text-[10px] text-white/40 font-bold tracking-widest">CHANGED YOUR PICKS? SEND AGAIN</button>
          </div>
        )}
        {picks.length === 0 ? (
          <div data-testid="order-sheet-empty">
            <h3 className="font-display text-3xl font-bold tracking-tight mt-2">{t("order.emptyTitle")}</h3>
            <p className="text-sm text-white/60 mt-2">{t("order.emptyDesc")}</p>
            <DineInPanel orderable={[]} />
          </div>
        ) : (
          <>
            <h3 className="font-display text-3xl font-bold tracking-tight mt-2">{t("order.showServer")}</h3>
            <p className="text-sm text-white/60 mt-1">{t("order.sub")}</p>
            {pending && (
              <div data-testid="picks-offline-chip" className="mt-3 flex items-center gap-2 rounded-xl border border-[#E25822]/50 bg-[#E25822]/10 px-3 py-2">
                <span className="w-2 h-2 rounded-full bg-[#E25822] shrink-0" />
                <p className="text-[11px] text-white/80 leading-snug">Saved on this device — it will sync automatically when you're back online.</p>
              </div>
            )}
            <div className="mt-4 divide-y divide-white/5 border-y border-white/10">
              {orderable.map((p) => (
                <div key={p.slug} data-testid={`pick-row-${p.slug}`} className="flex items-center gap-3 py-3">
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="w-11 h-11 rounded-lg object-cover" />
                  ) : (
                    <span className="w-11 h-11 rounded-lg bg-[#1A1A1A] border border-white/10 shrink-0" aria-hidden="true" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold leading-tight line-clamp-2">{p.name}</p>
                    <p className="text-[11px] text-[#D4AF37] font-bold mt-0.5">{`$${(p.price * p.qty).toFixed(2)}`}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`pick-minus-${p.slug}`} onClick={() => setPickQty(p.slug, p.qty - 1)} aria-label="Less"
                      className="w-8 h-8 rounded-full bg-[#1A1A1A] border border-white/15 text-base">−</button>
                    <span data-testid={`pick-qty-${p.slug}`} className="w-5 text-center text-sm font-bold">{p.qty}</span>
                    <button data-testid={`pick-plus-${p.slug}`} onClick={() => setPickQty(p.slug, p.qty + 1)} aria-label="More"
                      className="w-8 h-8 rounded-full bg-[#1A1A1A] border border-white/15 text-base">+</button>
                  </div>
                </div>
              ))}
            </div>
            <p data-testid="till-note" className="mt-4 text-xs text-white/55 text-center">
              Tax and anything else is added at the till.
            </p>
            <DineInPanel orderable={orderable} onSent={(slugs) => slugs.forEach((s) => setPickQty(s, 0))} />
            {checkoutUrl && orderable.length > 0 && !sent && (
              <>
                <button data-testid="picks-order-pickup" onClick={() => orderVia("pickup")} disabled={!!placing}
                  className="w-full mt-4 flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-bold rounded-full py-3.5 text-xs tracking-widest hover:bg-[#F1C40F] disabled:opacity-60">
                  <ExternalLink size={13} /> {placing === "pickup" ? "OPENING CHECKOUT…" : "ORDER PICKUP — PREPAY ONLINE"}
                </button>
                <button data-testid="picks-order-delivery" onClick={() => orderVia("delivery")} disabled={!!placing}
                  className="w-full mt-2.5 flex items-center justify-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] font-bold rounded-full py-3.5 text-xs tracking-widest hover:bg-[#D4AF37]/10 disabled:opacity-60">
                  <ExternalLink size={13} /> {placing === "delivery" ? "OPENING CHECKOUT…" : "ORDER DELIVERY — PREPAY ONLINE"}
                </button>
                <p className="text-[10px] text-white/40 mt-2 text-center leading-relaxed">
                  Your picks carry straight over to tryurbanturban.com — prepay there and your order prints to our kitchen.
                  Pickup is ready at the front desk. No payment ever happens in this app. Dining in? Just show this list to your server.
                </p>
              </>
            )}
            {grocery.length > 0 && (
              <div data-testid="picks-grocery-section" className="mt-4 rounded-2xl border border-[#8FB4D9]/30 bg-[#0E1319] p-4">
                <p className="text-[10px] tracking-[0.25em] text-[#8FB4D9] font-black">SELF-SERVICE · URBAN GROCERY</p>
                <p className="text-[10px] text-white/45 mt-1 leading-relaxed">Grocery picks aren't part of online orders — grab them from the shelves and pay at the store register.</p>
                <div className="mt-2 divide-y divide-white/5">
                  {grocery.map((p) => (
                    <div key={p.slug} data-testid={`grocery-row-${p.slug}`} className="flex items-center justify-between gap-2 py-2 text-xs">
                      <span className="font-semibold min-w-0 truncate">{p.name} × {p.qty}</span>
                      <button data-testid={`grocery-remove-${p.slug}`} onClick={() => setPickQty(p.slug, 0)}
                        className="text-white/40 text-[10px] font-bold shrink-0">REMOVE</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button data-testid="picks-clear" onClick={clearPicks}
              className="w-full mt-4 border border-white/20 text-white/60 text-xs font-bold rounded-full py-3 tracking-widest">
              {t("order.clear")}
            </button>
          </>
        )}
        {orderInfo?.last_paid && !sent && (
          <div data-testid="picks-reorder-panel" className="mt-4 rounded-2xl border border-[#D4AF37]/30 bg-[#14100A] p-4">
            <p className="text-[10px] tracking-[0.25em] text-[#D4AF37] font-black">ORDER IT AGAIN</p>
            <p className="text-xs text-white/60 mt-1">
              Your last paid {orderInfo.last_paid.fulfillment} order — {orderInfo.last_paid.items.reduce((s, i) => s + i.qty, 0)} item{orderInfo.last_paid.items.reduce((s, i) => s + i.qty, 0) === 1 ? "" : "s"} · ${orderInfo.last_paid.total.toFixed(2)}
            </p>
            <button data-testid="picks-reorder-last" onClick={reorderLast} disabled={!!placing}
              className="mt-3 w-full flex items-center justify-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] font-bold rounded-full py-3 text-xs tracking-widest hover:bg-[#D4AF37]/10 disabled:opacity-60">
              <ExternalLink size={13} /> {placing === "reorder" ? "OPENING CHECKOUT…" : "REORDER IN ONE TAP"}
            </button>
          </div>
        )}
        {reviewUrl && (
          <div data-testid="order-review-card" className="mt-4 rounded-2xl border border-[#D4AF37]/30 bg-[#1C160A] p-4">
            <div className="flex items-center gap-1 text-[#D4AF37]" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={12} fill="currentColor" />)}
            </div>
            <p className="text-xs text-white/70 leading-relaxed mt-2">
              Your Google review reshapes our future in this community — 30 seconds from you helps this family kitchen keep going.
            </p>
            <a
              data-testid="order-review-btn"
              href={reviewUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => { track("google_review_tapped", { source: "order_sheet" }); setThanksOpen(true); }}
              className="mt-3 w-full flex items-center justify-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] text-[10px] font-bold rounded-full py-3 tracking-widest hover:bg-[#D4AF37]/10"
            >
              <Star size={12} fill="currentColor" /> REVIEW US ON GOOGLE
            </a>
          </div>
        )}
        <button
          data-testid="order-sheet-done"
          onClick={onClose}
          className="w-full mt-3 bg-[#D4AF37] text-black font-bold rounded-full py-3.5 text-sm tracking-widest hover:bg-[#F1C40F]"
        >
          {picks.length === 0 ? t("order.keep") : t("order.done")}
        </button>
        <ReviewThanks open={thanksOpen} onClose={() => setThanksOpen(false)} />
      </div>
    </div>
  );
};

const TopNavPages = () => {
  const { pages } = useContent();
  const links = pages.filter((p) => p.placement === "topnav");
  if (!links.length) return null;
  return (
    <nav data-testid="topnav-pages" className="hidden md:flex items-center gap-4 mr-2">
      {links.map((p) => (
        <NavLink key={p.slug} to={`/p/${p.slug}`} data-testid={`topnav-page-${p.slug}`}
          className="text-[11px] font-bold tracking-widest uppercase opacity-70 hover:opacity-100">
          {p.title}
        </NavLink>
      ))}
    </nav>
  );
};

const SiteBanner = () => {
  const { owner } = useContent();
  const text = useContentValue("site.banner", "");
  if (!text && !owner) return null;
  return (
    <div data-testid="site-banner" className="bg-[#D4AF37] text-black text-center text-xs font-bold tracking-wide px-4 py-2">
      <Editable k="site.banner" label="Site banner (empty = hidden)" as="span" />
    </div>
  );
};

const FootRow = ({ icon: Icon, k, label }) => {
  const { owner } = useContent();
  const v = useContentValue(k, "");
  if (!v && !owner) return null;
  return (
    <p className="flex items-center gap-2">
      <Icon size={13} className="text-[#D4AF37] shrink-0" />
      <Editable k={k} label={label} as="span" />
    </p>
  );
};

const SiteFooter = () => {
  const { pages, owner } = useContent();
  const note = useContentValue("site.footer_note", "");
  const links = pages.filter((p) => p.placement === "footer");
  return (
    <footer data-testid="site-footer" className="mt-12 px-6 py-8 border-t border-white/10 text-sm">
      <p className="font-display font-bold tracking-wide">URBAN <span className="text-[#D4AF37]">BY VEERGI</span></p>
      <div className="mt-3 space-y-1.5 opacity-70">
        <FootRow icon={Clock} k="site.hours" label="Hours" />
        <FootRow icon={MapPin} k="site.address" label="Address" />
        <FootRow icon={Phone} k="site.phone" label="Phone" />
      </div>
      {links.length > 0 && (
        <div data-testid="footer-pages" className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5">
          {links.map((p) => (
            <NavLink key={p.slug} to={`/p/${p.slug}`} data-testid={`footer-page-${p.slug}`}
              className="text-[11px] font-bold tracking-widest uppercase text-[#D4AF37]/80 hover:text-[#D4AF37]">
              {p.title}
            </NavLink>
          ))}
        </div>
      )}
      {(note || owner) && (
        <p className="mt-4 text-xs opacity-50">
          <Editable k="site.footer_note" label="Footer note" as="span" multiline />
        </p>
      )}
    </footer>
  );
};

const SeoFromContent = () => {
  const { overrides } = useContent();
  const { pathname } = useLocation();
  useEffect(() => {
    const t = overrides[`seo:${pathname}:title`];
    const d = overrides[`seo:${pathname}:desc`];
    if (t) document.title = t;
    if (d) {
      const m = document.querySelector('meta[name="description"]');
      if (m) m.content = d;
    }
  }, [overrides, pathname]);
  return null;
};

const navItems = [
  { to: "/experience", key: "nav.home", icon: Home, end: true, tid: "nav-home" },
  { to: "/menu", key: "nav.menu", icon: UtensilsCrossed, tid: "nav-menu" },
  { to: "/experience/brands", key: "nav.brands", icon: Store, tid: "nav-brands" },
  { to: "/experience/arcade", key: "nav.arcade", icon: Gamepad2, tid: "nav-arcade", flag: "arcade" },
  { to: "/experience/passport", key: "nav.passport", icon: BookUser, tid: "nav-passport", flag: "passport" },
];

const BottomNav = () => {
  const flags = useFlags();
  const t = useT();
  const { pages } = useContent();
  const navPages = pages.filter((p) => p.placement === "bottomnav").slice(0, 2);
  const [count, setCount] = useState(picksCount);
  useEffect(() => {
    const sync = () => setCount(picksCount());
    window.addEventListener("xp-picks-changed", sync);
    return () => window.removeEventListener("xp-picks-changed", sync);
  }, []);
  return (
    <nav
      data-testid="bottom-nav"
      className="xp-nav fixed bottom-0 z-50 w-full h-[72px] backdrop-blur-xl flex justify-around items-center px-2"
    >
      {navItems.filter(({ flag }) => flagOn(flags, flag)).map(({ to, key, icon: Icon, end, tid }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          data-testid={tid}
          aria-label={t(key)}
          className={({ isActive }) =>
            `flex flex-col items-center gap-1 min-w-[52px] py-2 transition-colors ${isActive ? "xp-nav-active" : "xp-nav-idle"}`
          }
        >
          <Icon size={20} strokeWidth={1.8} />
          <span className="text-[10px] tracking-widest font-semibold">{t(key)}</span>
        </NavLink>
      ))}
      {navPages.map((p) => (
        <NavLink key={p.slug} to={`/p/${p.slug}`} data-testid={`bottomnav-page-${p.slug}`} aria-label={p.title}
          className={({ isActive }) =>
            `flex flex-col items-center gap-1 min-w-[52px] py-2 transition-colors ${isActive ? "xp-nav-active" : "xp-nav-idle"}`
          }>
          <FileText size={20} strokeWidth={1.8} />
          <span className="text-[10px] tracking-widest font-semibold uppercase truncate max-w-[70px]">{p.title}</span>
        </NavLink>
      ))}
      <button
        data-testid="nav-order"
        aria-label="ORDER"
        onClick={() => orderHandoff(undefined, "bottom_nav")}
        className="relative flex flex-col items-center gap-1 min-w-[52px] py-2 xp-nav-idle"
      >
        <ShoppingBag size={20} strokeWidth={1.8} />
        {count > 0 && (
          <span data-testid="nav-order-badge" className="absolute top-0.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D4AF37] text-black text-[10px] font-black flex items-center justify-center">
            {count}
          </span>
        )}
        <span className="text-[9px] tracking-widest font-semibold">{t("nav.order")}</span>
      </button>
    </nav>
  );
};

const TableChip = () => {
  const [table, setTable] = useState(() => localStorage.getItem("xp_table") || "");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  useEffect(() => {
    const sync = () => setTable(localStorage.getItem("xp_table") || "");
    window.addEventListener("xp-table-changed", sync);
    return () => window.removeEventListener("xp-table-changed", sync);
  }, []);
  const saveTable = (v) => {
    const val = v.trim();
    if (val) localStorage.setItem("xp_table", val);
    else localStorage.removeItem("xp_table");
    window.dispatchEvent(new Event("xp-table-changed"));
    track("table_chip_changed", { table: val });
    setEditing(false);
  };
  if (!table && !editing) return null;
  return (
    <>
      {table && (
        <button
          data-testid="table-chip"
          aria-label={`Table ${table} — tap to change`}
          onClick={() => { setDraft(table); setEditing(true); }}
          className="fixed z-[60] right-3 bottom-[80px] flex items-center gap-1.5 rounded-full bg-[#1A1A1A]/95 border border-[#D4AF37]/60 px-3.5 py-2 backdrop-blur-md shadow-lg"
        >
          <HandPlatter size={13} className="text-[#D4AF37]" />
          <span className="text-[11px] font-black tracking-widest text-[#D4AF37]">TABLE {table}</span>
        </button>
      )}
      {editing && (
        <div data-testid="table-chip-modal" className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center px-6" onClick={() => setEditing(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xs bg-[#121212] rounded-3xl border border-[#D4AF37]/40 p-6 text-left">
            <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-black">YOUR TABLE</p>
            <p className="text-xs text-white/55 mt-1.5 leading-relaxed">Moved tables or scanned the wrong card? Fix it here so your food finds you.</p>
            <input
              data-testid="table-chip-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, 6))}
              placeholder="Table #"
              autoFocus
              className="w-full mt-4 bg-[#1A1A1A] border border-white/15 rounded-xl px-4 py-3 text-lg font-bold text-center focus:outline-none focus:border-[#D4AF37]/70"
            />
            <button data-testid="table-chip-save" onClick={() => saveTable(draft)} disabled={!draft.trim()}
              className="w-full mt-3 bg-[#D4AF37] text-black font-bold rounded-full py-3 text-xs tracking-widest disabled:opacity-50">
              SAVE TABLE
            </button>
            <button data-testid="table-chip-clear" onClick={() => saveTable("")}
              className="w-full mt-2 border border-white/20 text-white/60 font-bold rounded-full py-3 text-[10px] tracking-widest">
              I'M NOT AT A TABLE
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const readyChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const note = (freq, at, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + at + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + dur);
      o.start(ctx.currentTime + at);
      o.stop(ctx.currentTime + at + dur + 0.05);
    };
    note(880, 0, 0.4);
    note(1318.5, 0.22, 0.6);
    setTimeout(() => ctx.close().catch(() => {}), 1600);
  } catch { /* audio unavailable */ }
};

const ReadyWatcher = () => {
  const { rawSoundOn } = useSound();
  const soundRef = useRef(rawSoundOn);
  soundRef.current = rawSoundOn;
  useEffect(() => {
    const check = () => {
      api.get(`/checkout/mine?user_id=${USER_ID}`).then((r) => {
        const a = r.data.active;
        if (!a || a.status !== "ready" || localStorage.getItem("xp_ready_notified") === a.code) return;
        localStorage.setItem("xp_ready_notified", a.code);
        track("order_ready_buzz", { code: a.code, fulfillment: a.fulfillment });
        if (soundRef.current) readyChime();
        toast.success(
          a.fulfillment === "pickup"
            ? `YOUR ORDER IS READY — grab it at the front desk! · #${a.code}`
            : `YOUR ORDER IS READY — it's heading to your driver · #${a.code}`,
          { duration: 12000, style: { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.5)" } }
        );
      }).catch(() => {});
    };
    check();
    const t = setInterval(check, 30000);
    return () => clearInterval(t);
  }, []);
  return null;
};

const CrashProbe = () => {
  if (process.env.NODE_ENV !== "production" && new URLSearchParams(window.location.search).get("crash") === "1") {
    throw new Error("crash probe — error boundary test");
  }
  return null;
};

export default function ExperienceLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const checkedIn = useRef(false);
  const idleTimer = useRef(null);
  const [orderOpen, setOrderOpen] = useState(false);
  const [attract, setAttract] = useState(false);
  const pref = useThemePref();
  // admin screens are PERMANENTLY light (owner ruling); guests get their remembered choice.
  // The cinematic film screen is a cinema — always dark, like the photos it plays over.
  const isAdmin = location.pathname.startsWith("/experience/admin");
  const isCinema = location.pathname.startsWith("/experience/cinematic");
  const theme = isAdmin || (pref === "light" && !isCinema) ? "theme-light" : "theme-dark";

  useEffect(() => {
    document.documentElement.classList.toggle("theme-light-root", theme === "theme-light");
  }, [theme]);

  useEffect(() => {
    track("experience_open", { path: location.pathname });
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // in-app order sheet (V1: no POS links in guest UI)
  useEffect(() => {
    const onOrder = () => setOrderOpen(true);
    window.addEventListener("xp-order", onOrder);
    return () => window.removeEventListener("xp-order", onOrder);
  }, []);

  // offline order sync: retry pending picks when the tablet reconnects
  useEffect(() => {
    resyncPicks();
    window.addEventListener("online", resyncPicks);
    return () => window.removeEventListener("online", resyncPicks);
  }, []);

  // kiosk idle reset to start screen + attract showcase
  const resetIdle = useCallback(() => {
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      window.__xpStoryAudio?.pause();
      window.__xpStoryAudio = null;
      setOrderOpen(false);
      sessionStorage.removeItem("xp_admin_ok");
      navigate("/experience");
      window.scrollTo(0, 0);
      setAttract(true);
      track("idle_reset", {});
    }, IDLE_TIMEOUT_MS);
  }, [navigate]);

  useEffect(() => {
    const events = ["pointerdown", "touchstart", "keydown", "scroll"];
    events.forEach((e) => window.addEventListener(e, resetIdle, { passive: true }));
    resetIdle();
    return () => {
      clearTimeout(idleTimer.current);
      events.forEach((e) => window.removeEventListener(e, resetIdle));
    };
  }, [resetIdle]);

  useEffect(() => {
    if (checkedIn.current) return;
    checkedIn.current = true;
    const params = new URLSearchParams(window.location.search);
    const qsTable = params.get("table");
    if (qsTable) {
      localStorage.setItem("xp_table", qsTable);
      window.dispatchEvent(new Event("xp-table-changed"));
      track("table_qr_scan", { table: qsTable });
      toast.success(`Welcome to Table ${qsTable} — browse and order right here`, {
        duration: 6000,
        style: { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" },
      });
    }
    if (params.get("attract") === "1") setAttract(true);
    api.post("/streak/checkin", { user_id: USER_ID }).then((r) => {
      if (r.data.awarded) {
        toast.success(`🔥 ${r.data.streak}-DAY STREAK · +${r.data.points} URBAN POINTS`, {
          style: { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" },
        });
      }
    }).catch(() => {});
  }, []);

  return (
    <FlagsProvider>
      <SoundProvider>
        <LangProvider>
        <ContentProvider>
        <div className={`xp-root min-h-screen flex justify-center ${theme}`} style={{ background: "#111111" }}>
          <div className={`w-full relative min-h-screen pb-40 text-left ${theme}`}>
            <Header />
            <SiteBanner />
            <SeoFromContent />
            <ErrorBoundary key={location.pathname}>
              <CrashProbe />
              <Outlet />
            </ErrorBoundary>
            {!isAdmin && <ErrorBoundary key="site-footer" silent><SiteFooter /></ErrorBoundary>}
            <BottomNav />
            <ErrorBoundary key="table-chip" silent>
              <TableChip />
            </ErrorBoundary>
            <ErrorBoundary key="mascot" silent>
              <Mascot />
            </ErrorBoundary>
            <ErrorBoundary key="ready-watcher" silent>
              <ReadyWatcher />
            </ErrorBoundary>
            <OrderSheet open={orderOpen} onClose={() => setOrderOpen(false)} />
            <AttractScreen open={attract} onDismiss={() => { setAttract(false); track("attract_dismissed", {}); }} />
          </div>
        </div>
        </ContentProvider>
        </LangProvider>
      </SoundProvider>
    </FlagsProvider>
  );
}
